import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Calendar from '@/components/ui-v2/date/calendar';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  formatIsoDate,
  formatLocalizedDate,
  getLocalizedPlaceholder,
  parseDateValue,
  parseLocalizedInputValue,
} from '@/lib/date-utils';
import { cn } from '@/lib/utils';
import { CalendarDays } from 'lucide-react';

export interface DateInputProps extends Omit<
  React.ComponentProps<typeof Input>,
  'type' | 'value' | 'onChange'
> {
  value?: string | null;
  onDateChange?: (isoValue: string | null) => void;
  language?: string;
  triggerAriaLabel?: string;
}

function DateInput(
  {
    value,
    onDateChange,
    language: languageProp,
    placeholder,
    className,
    onBlur,
    onKeyDown,
    disabled,
    triggerAriaLabel,
    ...props
  }: DateInputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const { i18n, t } = useTranslation();
  const language = languageProp ?? i18n.language;
  const sanitizedValue = value ?? null;

  const lastDisplayRef = React.useRef('');
  const blurSkipRef = React.useRef(false);
  const pendingBlurRef = React.useRef(false);

  const [displayValue, setDisplayValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const formatted = formatLocalizedDate(sanitizedValue, language);
    setDisplayValue(formatted);
    lastDisplayRef.current = formatted;
  }, [language, sanitizedValue]);

  const commitValue = React.useCallback(() => {
    const candidate = displayValue.trim();
    if (!candidate) {
      if (sanitizedValue !== null) {
        onDateChange?.(null);
      }
      setDisplayValue('');
      lastDisplayRef.current = '';
      return;
    }

    const parsed = parseLocalizedInputValue(candidate, language);
    if (!parsed) {
      setDisplayValue(lastDisplayRef.current);
      return;
    }

    const iso = formatIsoDate(parsed);
    if (!iso) return;

    const formatted = formatLocalizedDate(parsed, language);
    setDisplayValue(formatted);
    lastDisplayRef.current = formatted;

    if (iso !== sanitizedValue) {
      onDateChange?.(iso);
    }
  }, [displayValue, language, onDateChange, sanitizedValue]);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    commitValue();
    if (blurSkipRef.current) {
      blurSkipRef.current = false;
      pendingBlurRef.current = true;
      return;
    }
    pendingBlurRef.current = false;
    onBlur?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      commitValue();
    }
    if (event.key === 'Escape') {
      setDisplayValue(lastDisplayRef.current);
      setIsOpen(false);
    }
    onKeyDown?.(event);
  };

  const handleSelectDate = (date: Date | undefined) => {
    const normalized = date ?? null;
    if (!normalized) return;

    const iso = formatIsoDate(normalized);
    if (!iso) return;

    const formatted = formatLocalizedDate(normalized, language);
    setDisplayValue(formatted);
    lastDisplayRef.current = formatted;
    setIsOpen(false);

    if (iso !== sanitizedValue) {
      onDateChange?.(iso);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(event.target.value);
  };

  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    if (!next && pendingBlurRef.current) {
      pendingBlurRef.current = false;
      onBlur?.({} as React.FocusEvent<HTMLInputElement>);
    }
    setIsOpen(next);
  };

  const handleTriggerMouseDown = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    blurSkipRef.current = true;
  };

  const selectedDate = parseDateValue(sanitizedValue, language);
  const resolvedPlaceholder = placeholder ?? getLocalizedPlaceholder(language);

  const triggerLabel = triggerAriaLabel ?? t('general.open_calendar');

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <div className="relative">
        <Input
          ref={ref}
          type="text"
          inputMode="numeric"
          className={cn('pr-10', className)}
          value={displayValue}
          placeholder={resolvedPlaceholder}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          {...props}
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            aria-label={triggerLabel}
            disabled={disabled}
            onMouseDown={handleTriggerMouseDown}
          >
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="h-[360px] p-0" side="bottom" align="end">
        <Calendar
          key={selectedDate?.toISOString() ?? 'default-month'}
          mode="single"
          selected={selectedDate ?? undefined}
          defaultMonth={selectedDate ?? undefined}
          className="h-full"
          onSelect={handleSelectDate}
        />
      </PopoverContent>
    </Popover>
  );
}

export default React.forwardRef<HTMLInputElement, DateInputProps>(DateInput);
